// Playable URL for a target word's pronunciation.
//
// Dictionary audio (TTS backfill; migration 130): `get_due_vocabulary` returns
// the gender-NEUTRAL base "<lang>/<hash>" as the card's `audioPath`. We append
// "_<f|m>.mp3" per the user's voiceGender setting and prefix the Supabase
// Storage public path. The bucket is public + CORS-open, so a plain
// <audio>/new Audio(url) plays it in the browser with no auth.
//
// Private audio (Anki import; migration 176): `words.audio_path` is
// "private:<uid>/<batch>/<sha1>.<ext>" in the private `user-audio` bucket.
// It plays as-is (no gender suffix) through a signed URL, which needs a
// round trip — hence the async `resolveAudioUrl` / `useAudioUrl`.

import { supabase } from './supabase';
import { canPlayAudio } from './audioPlayable';

export type VoiceGender = 'f' | 'm';

const base = (import.meta.env.VITE_SUPABASE_URL ?? '').replace(/\/+$/, '');

export const PRIVATE_AUDIO_PREFIX = 'private:';
export const PRIVATE_AUDIO_BUCKET = 'user-audio';

export const isPrivateAudio = (audioPath: string | undefined | null): boolean =>
  !!audioPath && audioPath.startsWith(PRIVATE_AUDIO_PREFIX);

// Which clips this browser can play lives in audioPlayable.ts (no Supabase
// import, so the study store and its tests can use it).
export { browserPlaysOgg, canPlayAudio, isOggAudio, setOggSupportForTests, unplayableHere } from './audioPlayable';

/**
 * Builds the playable mp3 URL for a card's DICTIONARY audio base path.
 * Returns undefined when the word hasn't been voiced (no audioPath) or when
 * the audio is private (use resolveAudioUrl for those).
 */
export function audioUrl(audioPath: string | undefined, gender: VoiceGender = 'f'): string | undefined {
  if (!audioPath || base === '' || isPrivateAudio(audioPath)) return undefined;
  return `${base}/storage/v1/object/public/audio/${audioPath}_${gender}.mp3`;
}

const SIGNED_TTL_S = 3600;
const signed = new Map<string, { url: string; until: number }>();

/** Signed URL of a private audio object (cached until 5 min before expiry). */
async function signedUrl(path: string): Promise<string | undefined> {
  const hit = signed.get(path);
  if (hit && hit.until > Date.now()) return hit.url;
  const { data, error } = await supabase.storage.from(PRIVATE_AUDIO_BUCKET).createSignedUrl(path, SIGNED_TTL_S);
  if (error || !data?.signedUrl) return undefined;
  signed.set(path, { url: data.signedUrl, until: Date.now() + (SIGNED_TTL_S - 300) * 1000 });
  return data.signedUrl;
}

/** Playable URL for any audio path — dictionary (sync) or private (signed). */
export async function resolveAudioUrl(audioPath: string | undefined, gender: VoiceGender = 'f'): Promise<string | undefined> {
  if (!audioPath || !canPlayAudio(audioPath)) return undefined;
  if (isPrivateAudio(audioPath)) return signedUrl(audioPath.slice(PRIVATE_AUDIO_PREFIX.length));
  return audioUrl(audioPath, gender);
}
